import { TAGS_HTML_RESTRITO } from '@/shared/components/ui/common/SanitizedHtml';

const TAGS_PRESERVADAS = new Set<string>(TAGS_HTML_RESTRITO);

function ehPreservada(elemento: Element): boolean {
  return TAGS_PRESERVADAS.has(elemento.tagName.toLowerCase());
}

/**
 * O único filho-elemento de `no`, desde que `no` não tenha texto solto ao lado dele — texto solto
 * viraria conteúdo órfão se descêssemos um nível.
 */
function unicoFilhoElemento(no: Element): Element | null {
  const filhos = Array.from(no.childNodes);
  const temTextoSolto = filhos.some(
    (filho) => filho.nodeType === filho.TEXT_NODE && (filho.textContent ?? '').trim().length > 0,
  );
  const elementos = filhos.filter(
    (filho): filho is Element => filho.nodeType === filho.ELEMENT_NODE,
  );
  return elementos.length === 1 && !temTextoSolto ? elementos[0] : null;
}

/**
 * E-mail costuma vir embrulhado em `<div>`/`<center>` de layout. Descer por esses invólucros é
 * visualmente inócuo (o perfil restrito os descarta preservando o conteúdo) e é o que transforma
 * "um nó de topo só" em vários nós fatiáveis. Invólucro que o perfil PRESERVA — `<table>`, o
 * layout clássico de e-mail — é onde a descida para: ali o embrulho é conteúdo.
 */
function descerAteOsNosDeTopo(body: HTMLElement): Element {
  let raiz: Element = body;
  let filho = unicoFilhoElemento(raiz);
  while (filho && !ehPreservada(filho) && filho.children.length > 0) {
    raiz = filho;
    filho = unicoFilhoElemento(raiz);
  }
  return raiz;
}

function fatiaDoNo(no: ChildNode): string {
  if (no.nodeType === no.ELEMENT_NODE) return (no as Element).outerHTML;
  if (no.nodeType !== no.TEXT_NODE) return '';
  const texto = (no.textContent ?? '').trim();
  return texto ? texto.replace(/&/g, '&amp;').replace(/</g, '&lt;') : '';
}

/**
 * Quebra a cópia do e-mail em sub-blocos pagináveis, um por nó de topo.
 *
 * A paginação do documento nunca fatiou bloco atômico: um bloco mais alto que a página é
 * empurrado inteiro, e o excedente é cortado pelo `overflow-hidden` da folha A4 — em silêncio. A
 * réplica do e-mail é o bloco mais alto do certificado e é justamente a CÓPIA ÍNTEGRA do que foi
 * enviado, o fato que a seção existe para provar. Entregando N blocos em vez de 1, o mecanismo de
 * distribuição que já existe faz o resto.
 *
 * Fora do browser (SSR) e quando não há como fatiar (e-mail dentro de uma `<table>` única), o
 * retorno é o HTML inteiro numa fatia — o comportamento de antes, nunca conteúdo perdido.
 */
export function fatiarConteudoEmail(html: string): string[] {
  if (typeof DOMParser === 'undefined') return [html];

  const documento = new DOMParser().parseFromString(html, 'text/html');
  const raiz = descerAteOsNosDeTopo(documento.body);
  const fatias = Array.from(raiz.childNodes).map(fatiaDoNo).filter(Boolean);

  return fatias.length > 0 ? fatias : [html];
}
